/**
 * Use Case Step Component
 *
 * Lets the user select which AI features they want to use.
 * Multi-select card grid with disk space estimates per use case.
 *
 * The list is fetched from the backend (`GET /api/v1/setup/use-cases`,
 * constants/setup/use_cases.py::USE_CASE_DEFINITIONS) rather than
 * hardcoded here. A previous hardcoded USE_CASES array duplicated the
 * backend definitions and had already drifted (create_audio's icon was
 * "volume" here vs "volume-2" on the backend) -- CLAUDE.md RULE #-2
 * forbids keeping both, so the backend list is now the single source of
 * truth and this component is a pure renderer over it.
 */

import { useCallback, useEffect, useState } from 'react';

import { useTranslation } from '@/hooks/useTranslation';
import { getUseCases } from '@/services/setupService';
import type { UseCaseDefinition as BackendUseCaseDefinition } from '@/services/setupService';

type UseCaseIcon = 'video' | 'volume-2' | 'image' | 'brain' | 'mic' | 'file' | 'users' | 'music';

const KNOWN_ICONS: readonly UseCaseIcon[] = [
  'video',
  'volume-2',
  'image',
  'brain',
  'mic',
  'file',
  'users',
  'music',
];

/** Fall back to a generic icon if the backend ever sends one this component doesn't render yet. */
function toUseCaseIcon(icon: string): UseCaseIcon {
  return (KNOWN_ICONS as readonly string[]).includes(icon) ? (icon as UseCaseIcon) : 'file';
}

/**
 * snake_case backend id -> camelCase i18n key segment, e.g.
 * "sound_effects" -> "soundEffects". Every existing use case id already
 * maps 1:1 onto its translation key under wizard.useCase.* this way, so
 * deriving it keeps the backend definition and the translation keys in
 * lockstep without a second id->key table to maintain.
 */
function toCamelCase(snakeCaseId: string): string {
  return snakeCaseId.replace(/_([a-z0-9])/g, (_match, char: string) => char.toUpperCase());
}

interface UseCaseDefinition {
  id: string;
  titleKey: string;
  titleDefault: string;
  descKey: string;
  descDefault: string;
  icon: UseCaseIcon;
  estimateGb: number;
}

function toViewModel(uc: BackendUseCaseDefinition): UseCaseDefinition {
  const keyPrefix = `wizard.useCase.${toCamelCase(uc.id)}`;
  return {
    id: uc.id,
    titleKey: `${keyPrefix}.title`,
    titleDefault: uc.name,
    descKey: `${keyPrefix}.desc`,
    descDefault: uc.description,
    icon: toUseCaseIcon(uc.icon),
    estimateGb: uc.diskEstimateGb,
  };
}

interface UseCaseIconProps {
  name: UseCaseIcon;
  className?: string;
}

function UseCaseIconSvg({ name, className = 'w-6 h-6' }: UseCaseIconProps): React.ReactElement {
  switch (name) {
    case 'video':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 10l4.553-2.276A1 1 0 0121 8.723v6.554a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"
          />
        </svg>
      );
    case 'volume-2':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15.536 8.464a5 5 0 010 7.072M12 6a7 7 0 010 12M8.464 8.464a5 5 0 000 7.072M5 12a9 9 0 0114 0"
          />
        </svg>
      );
    case 'image':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      );
    case 'brain':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
          />
        </svg>
      );
    case 'mic':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
          />
        </svg>
      );
    case 'file':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
      );
    case 'users':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
      );
    case 'music':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 18V5l12-2v13M9 18a3 3 0 11-6 0 3 3 0 016 0zm12-2a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      );
  }
}

interface CheckmarkIconProps {
  className?: string;
}

function CheckmarkIcon({ className = 'w-5 h-5' }: CheckmarkIconProps): React.ReactElement {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  );
}

interface UseCaseCardProps {
  useCase: UseCaseDefinition;
  isSelected: boolean;
  onToggle: (id: string) => void;
  t: (key: string, params?: Record<string, string | number | boolean>) => string;
}

function UseCaseCard({ useCase, isSelected, onToggle, t }: UseCaseCardProps): React.ReactElement {
  return (
    <button
      onClick={() => onToggle(useCase.id)}
      className={`
        w-full text-left p-4 rounded-xl border transition-all min-h-[44px]
        ${
          isSelected
            ? 'bg-purple-900/30 border-purple-500/50'
            : 'bg-black/20 border-white/10 hover:border-white/20 hover:bg-black/30'
        }
      `}
      aria-pressed={isSelected}
    >
      <div className="flex items-start gap-3">
        <div
          className={`
            flex-shrink-0 p-2 rounded-lg
            ${isSelected ? 'bg-purple-500/20 text-purple-300' : 'bg-white/5 text-white/50'}
          `}
        >
          <UseCaseIconSvg name={useCase.icon} className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span
              className={`font-semibold text-sm ${isSelected ? 'text-white' : 'text-white/80'}`}
            >
              {t(useCase.titleKey, { defaultValue: useCase.titleDefault })}
            </span>
            {isSelected && <CheckmarkIcon className="w-4 h-4 text-purple-400 flex-shrink-0" />}
          </div>
          <p className="text-xs text-white/50 mt-1 leading-relaxed">
            {t(useCase.descKey, { defaultValue: useCase.descDefault })}
          </p>
          <p className="text-xs text-white/30 mt-1">
            {t('wizard.useCase.diskEstimate', {
              defaultValue: '~{{gb}} GB',
              gb: useCase.estimateGb,
            })}
          </p>
        </div>
      </div>
    </button>
  );
}

function SpinnerIcon(): React.ReactElement {
  return (
    <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary-500 border-t-transparent mx-auto mb-3" />
  );
}

interface UseCaseStepProps {
  selectedUseCases: string[];
  onSelectionChange: (useCases: string[]) => void;
  onNext: () => void;
  onBack: () => void;
}

export function UseCaseStep({
  selectedUseCases,
  onSelectionChange,
  onNext,
  onBack,
}: UseCaseStepProps): React.ReactElement {
  const { t } = useTranslation();
  const [useCases, setUseCases] = useState<UseCaseDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadUseCases = useCallback(() => {
    setIsLoading(true);
    setLoadError(null);
    getUseCases()
      .then((data) => {
        setUseCases(data.map(toViewModel));
      })
      .catch((e: unknown) => {
        setLoadError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    loadUseCases();
  }, [loadUseCases]);

  const handleToggle = (id: string): void => {
    if (selectedUseCases.includes(id)) {
      onSelectionChange(selectedUseCases.filter((uc) => uc !== id));
    } else {
      onSelectionChange([...selectedUseCases, id]);
    }
  };

  const canContinue = selectedUseCases.length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="section-heading-xl">
          {t('wizard.useCase.title', { defaultValue: 'What would you like to do?' })}
        </h2>
        <p className="text-white/60">
          {t('wizard.useCase.subtitle', {
            defaultValue: 'Select all that apply. You can always add more later.',
          })}
        </p>
      </div>

      {isLoading && (
        <div className="text-center py-8">
          <SpinnerIcon />
          <p className="text-white/50 text-sm">
            {t('wizard.useCase.loading', { defaultValue: 'Loading use cases...' })}
          </p>
        </div>
      )}

      {!isLoading && loadError && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4 text-center space-y-3">
          <p className="text-sm text-red-300">
            {t('wizard.useCase.loadError', {
              defaultValue: 'Failed to load use cases: {{error}}',
              error: loadError,
            })}
          </p>
          <button
            onClick={loadUseCases}
            className="px-4 py-2 text-sm bg-red-500/20 hover:bg-red-500/30 text-red-100 rounded-lg transition-colors min-h-[44px]"
          >
            {t('wizard.useCase.retry', { defaultValue: 'Retry' })}
          </button>
        </div>
      )}

      {/* Use case grid */}
      {!isLoading && !loadError && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {useCases.map((useCase) => (
            <UseCaseCard
              key={useCase.id}
              useCase={useCase}
              isSelected={selectedUseCases.includes(useCase.id)}
              onToggle={handleToggle}
              t={t}
            />
          ))}
        </div>
      )}

      {/* Selection summary */}
      {selectedUseCases.length > 0 && (
        <div className="bg-black/20 border border-white/10 rounded-xl px-4 py-3">
          <p className="text-sm text-white/60">
            {t('wizard.useCase.selectedCount', {
              defaultValue: '{{count}} selected',
              count: selectedUseCases.length,
            })}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-2">
        <button
          onClick={onBack}
          className="px-4 py-2 text-white/50 hover:text-white/80 transition-colors min-h-[44px]"
        >
          {t('wizard.back', { defaultValue: 'Back' })}
        </button>
        <button
          onClick={onNext}
          disabled={!canContinue}
          className="px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-600/40 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors min-h-[44px]"
        >
          {t('wizard.continue', { defaultValue: 'Continue' })}
        </button>
      </div>
    </div>
  );
}

export default UseCaseStep;
